import { useSSRHook } from "ssr-hook";

import { Event } from "../../types";
import moment from "moment";
import Icon from "@mdi/react";
import { mdiCalendar, mdiCalendarMonth, mdiDelete, mdiDownload, mdiImage, mdiMapMarker, mdiPencil, mdiUpload } from "@mdi/js";
import Modal from "react-responsive-modal";
import { useEffect, useRef, useState } from "react";
import { api } from "../services/api";
import { resizeImage } from "easy-image-resizer";
import { InputDate, Remove } from ".";
import { useUser } from "../User";
import { toBase64 } from "../services/common";

export function useEvents() {
    return useSSRHook<Event[]>("/api/events");
}

export function EventPreview({ event, onChange }: { event: Event, onChange: (event: null | Event) => void }) {
    const user = useUser();

    const [showDetail, setShowDetail] = useState(false);
    const [editEvent, setEditEvent] = useState(false);

    async function handleRemove() {
        await api.removeEvent({ eventId: event._id });
        setShowDetail(false);
        setEditEvent(false);
        onChange(null);
    }

    return <>
        <div className="event preview" onClick={() => setShowDetail(true)}>
            <div className="top" style={{ backgroundImage: `url(${event.picture.url})` }} />
            <div className="text">
                {event.place && <div className="place">
                    <Icon path={mdiMapMarker} size={0.8} /> {event.place}
                </div>}
                <div className="date">
                    <Icon path={mdiCalendarMonth} size={0.8} />
                    {moment(event.dateFrom).format("LL")}
                    {event.dateTo && ` - ${moment(event.dateTo).format("LL")}`}
                </div>
                {event.file && <div className="file">
                    <Icon path={mdiDownload} size={0.8} />
                    Soubor ke stažení
                </div>}
                <div className="divider" />
                <h3>{event.title}</h3>
                <p>{event.text}</p>
            </div>
        </div>

        <Modal
            open={showDetail}
            onClose={() => setShowDetail(false)}
            center
            styles={{ modal: { width: "550px" } }}
        >
            <div className="event">
                <div className="top" style={{ backgroundImage: `url(${event.picture.url})` }} />
                <div className="text">
                    {event.place && <div className="place">
                        <Icon path={mdiMapMarker} size={0.8} /> {event.place}
                    </div>}
                    <div className="date">
                        <Icon path={mdiCalendarMonth} size={0.8} />
                        {moment(event.dateFrom).format("LL")}
                        {event.dateTo && ` - ${moment(event.dateTo).format("LL")}`}
                    </div>
                    {event.file && <a href={event.file.url} target="__blank" className="file">
                        <Icon path={mdiDownload} size={0.8} />
                        Soubor ke stažení
                    </a>}
                    <div className="divider" />
                    <h3>{event.title}</h3>
                    <p>{event.text}</p>
                </div>
            </div>
            {(user.isEditor || user.isAdmin) && <div className="buttons">
                <button onClick={() => setEditEvent(true)}>
                    <Icon path={mdiPencil} size={1} /> Upravit
                </button>
                <Remove onRemove={() => handleRemove()} >Smazat</Remove>

                <EditEvent show={editEvent} event={event} onClose={() => setEditEvent(false)} onChange={onChange} />
            </div>}
        </Modal>
    </>;
}

export const emptyEvent: Event = {
    _id: "",
    title: "",
    dateFrom: moment().format("YYYY-MM-DD"),
    dateTo: null,
    text: "",
    place: "",
    picture: { type: "EASY_DB_FILE", url: "/logo.png" },
    file: null,
};

export function EditEvent({ show, event, onClose, onChange }: { show: boolean, event?: Event, onClose: () => void, onChange: (event: Event) => void }) {
    const [edit, setEdit] = useState<Event>(event || emptyEvent);
    const [message, setMessage] = useState("");

    const inputRef = useRef<HTMLInputElement>(null);
    const inputFileRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setEdit(event || emptyEvent);
        setMessage("");
    }, [event]);

    async function handleSave() {
        if (!edit.title) {
            setMessage("Vyplň aspoň nadpis!");
            return;
        }

        setMessage("Ukládám...");

        if (edit._id === "") {
            const [, error] = await api.addEvent(edit);
            if (error) {
                setMessage("Přidání akce selhalo");
                return;
            }
            setMessage("Akce přidána");
        } else {
            const [, error] = await api.updateEvent(edit);
            if (error) {
                setMessage("Ukládání změn selhalo");
                return;
            }
            setMessage("Akce úspěšně uložena");
        }

        setEdit(emptyEvent);
        onChange(edit);
        onClose();
    }

    return <Modal
        open={show}
        onClose={onClose}
        center
        styles={{ modal: { width: "550px" } }}
    >
        <h2>
            {edit._id === "" ? "Přidat akci" : "Upravit akci"}
        </h2>
        {message && <p className="info">{message}</p>}
        {edit && <>

            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", paddingBottom: "32px" }}>
                <div className="new-image" style={{ backgroundImage: `url(${edit.picture.url})`, height: "320px" }} />
                <button onClick={() => inputRef.current?.click()}>
                    <Icon path={mdiImage} size={1} /> Změnit obrázek akce
                </button>
                <input
                    className="hidden"
                    ref={inputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={async (e) => {
                        const file = e.target.files?.[0] as File;
                        if (!file) return;

                        const url = await resizeImage(file, {
                            maxWidth: 560,
                            maxHeight: 760,
                            type: "webp",
                        });

                        setEdit({ ...edit, picture: { type: "EASY_DB_FILE", url } });
                    }}
                />
            </div>

            <input
                type="text"
                placeholder="Napis"
                value={edit.title}
                onChange={(e) => setEdit({ ...edit, title: e.target.value })}
            />
            <input
                type="text"
                placeholder="Místo"
                value={edit.place}
                onChange={(e) => setEdit({ ...edit, place: e.target.value })}
            />
            {edit.file === null && <button className="center" onClick={() => inputFileRef.current?.click()}>
                <Icon path={mdiUpload} size={1} />
                Nahrát soubor ke stažení
            </button>}
            {edit.file && <div className="buttons" style={{ marginBottom: 30 }}>
                <a href={edit.file.url} target="__blank" className="button">
                    <Icon path={mdiDownload} size={0.8} />
                    Soubor ke stažení
                </a>
                <button onClick={() => inputFileRef.current?.click()}>
                    <Icon path={mdiUpload} size={1} />
                    Změnit
                </button>
                <button onClick={() => setEdit({ ...edit, file: null })}>
                    <Icon path={mdiDelete} size={1} />
                </button>
            </div>}
            <input
                className="hidden"
                ref={inputFileRef}
                type="file"
                onChange={async (e) => {
                    const file = e.target.files?.[0] as File;
                    if (!file) return;

                    setEdit({ ...edit, file: { type: "EASY_DB_FILE", url: await toBase64(file) } });
                }}
            />

            <label>
                <input
                    type="checkbox"
                    checked={edit.dateTo !== null}
                    onChange={(e) => setEdit({ ...edit, dateTo: e.target.checked ? moment(edit.dateFrom).add(1, "day").format("YYYY-MM-DD") : null })}
                />
                Jedná se o vícedenní akci
            </label>
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                <InputDate
                    value={edit.dateFrom}
                    onChange={(dateFrom) => setEdit({ ...edit, dateFrom })}
                />
                {edit.dateTo !== null && <>
                    <div style={{ marginBottom: "20px" }}> až </div>
                    <InputDate
                        value={edit.dateTo}
                        onChange={(dateTo) => setEdit({ ...edit, dateTo })}
                    />
                </>}
            </div>

            <textarea
                style={{ minHeight: "250px" }}
                placeholder="Obsah"
                value={edit.text}
                onChange={(e) => setEdit({ ...edit, text: e.target.value })}
            />
            <button onClick={handleSave}>
                {edit._id === ""
                    ? "Přidat akci"
                    : "Uložit změny"}
            </button>
        </>}
    </Modal>;
}